import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = [
  'Snacks & Confectionery', 'Beverages', 'Dairy & Eggs', 'Bakery',
  'Frozen Foods', 'Pantry & Dry Goods', 'Meat & Deli',
  'Vitamins & Supplements', 'Pain & Medicines', 'Baby & Kids',
  'Sports & Nutrition', 'Skin & Beauty', 'Hair Care', 'Personal Care',
  'Household & Cleaning', 'Pet Food',
];

const FEATURES = [
  'Organic', 'Gluten Free', 'Dairy Free', 'Vegan', 'Vegetarian',
  'Halal', 'Low Sugar', 'High Protein', 'Australian Made',
  'Family Size', 'Value Pack',
];

function Stepper({ label, value, min = 0, max = 10, onChange }) {
  return (
    <div className="stepper-group">
      <span className="stepper-label">{label}</span>
      <div className="stepper">
        <button type="button" className="stepper-btn" onClick={() => onChange(Math.max(min, value - 1))}>−</button>
        <span className="stepper-value">{value}</span>
        <button type="button" className="stepper-btn" onClick={() => onChange(Math.min(max, value + 1))}>+</button>
      </div>
    </div>
  );
}

function ChipSelector({ options, selected, onToggle }) {
  return (
    <div className="chip-selector">
      {options.map(opt => (
        <button
          key={opt}
          type="button"
          className={`pref-chip ${selected.includes(opt) ? 'pref-chip--on' : ''}`}
          onClick={() => onToggle(opt)}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

export default function ProfilePage() {
  const { user, updateProfile, logout, refreshUser } = useAuth();

  useEffect(() => { refreshUser(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [form, setForm] = useState({
    name:       user?.name       || '',
    postcode:   user?.postcode   || '',
    suburb:     user?.suburb     || '',
    adults:     user?.family?.adults   ?? 1,
    children:   user?.family?.children ?? 0,
    categories: user?.preferences?.categories ?? [],
    features:   user?.preferences?.features   ?? [],
  });
  const [busy,  setBusy]  = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const beh = user?.behaviour || { itemsTracked: 0, topCategories: [], topStores: [] };

  function setField(field) {
    return e => { setSaved(false); setForm(f => ({ ...f, [field]: e.target.value })); };
  }

  function setNumber(field, value) {
    setSaved(false);
    setForm(f => ({ ...f, [field]: value }));
  }

  function toggleChip(field, value) {
    setSaved(false);
    setForm(f => ({
      ...f,
      [field]: f[field].includes(value)
        ? f[field].filter(v => v !== value)
        : [...f[field], value],
    }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setError(''); setSaved(false); setBusy(true);
    try {
      await updateProfile({
        name:     form.name,
        postcode: form.postcode,
        suburb:   form.suburb,
        family:      { adults: form.adults, children: form.children },
        preferences: { categories: form.categories, features: form.features },
      });
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="profile-page">

      {/* ── Identity card ── */}
      <div className="profile-card">
        <div className="profile-avatar">
          {(user?.name || user?.email || '?')[0].toUpperCase()}
        </div>
        <div className="profile-info">
          <h2 className="profile-name">{user?.name || 'No name set'}</h2>
          <p className="profile-email">{user?.email}</p>
          <div className="profile-badges">
            {user?.verified
              ? <span className="badge badge--green">Verified</span>
              : <span className="badge badge--yellow">Email not verified</span>}
            {user?.hasGoogle   && <span className="badge badge--blue">Google</span>}
            {user?.hasPassword && <span className="badge badge--muted">Email login</span>}
          </div>
        </div>
      </div>

      <form className="profile-form" onSubmit={handleSave}>

        {/* ── Personal details ── */}
        <h3 className="profile-section-title">Personal details</h3>
        <div className="form-group">
          <label>Display name</label>
          <input type="text" placeholder="Your name" value={form.name} onChange={setField('name')} />
        </div>
        <h3 className="profile-section-title">
          Your location
          <span className="profile-section-hint">Used to find the right store for accurate deal data</span>
        </h3>
        <div className="form-row">
          <div className="form-group">
            <label>Postcode</label>
            <input type="text" placeholder="e.g. 2000" value={form.postcode} onChange={setField('postcode')} maxLength={4} pattern="\d{4}" title="4-digit Australian postcode" />
          </div>
          <div className="form-group">
            <label>Suburb (optional)</label>
            <input type="text" placeholder="e.g. Sydney" value={form.suburb} onChange={setField('suburb')} />
          </div>
        </div>

        {/* ── Household ── */}
        <h3 className="profile-section-title">
          Your household
          <span className="profile-section-hint">Helps the AI suggest the right pack sizes and family deals</span>
        </h3>
        <div className="stepper-row">
          <Stepper label="Adults"   value={form.adults}   min={1} onChange={v => setNumber('adults', v)} />
          <Stepper label="Children" value={form.children} min={0} onChange={v => setNumber('children', v)} />
        </div>

        {/* ── Category interests ── */}
        <h3 className="profile-section-title">
          Shopping interests
          <span className="profile-section-hint">The AI prioritises these categories in suggestions</span>
        </h3>
        <ChipSelector
          options={CATEGORIES}
          selected={form.categories}
          onToggle={v => toggleChip('categories', v)}
        />

        {/* ── Product features ── */}
        <h3 className="profile-section-title">
          Dietary &amp; product preferences
          <span className="profile-section-hint">Filter suggestions to match your lifestyle</span>
        </h3>
        <ChipSelector
          options={FEATURES}
          selected={form.features}
          onToggle={v => toggleChip('features', v)}
        />

        {error && <p className="auth-error">{error}</p>}

        <div className="profile-actions">
          <button type="submit" className="btn-save" disabled={busy}>
            {busy ? 'Saving…' : saved ? '✓ Saved' : 'Save changes'}
          </button>
          <button type="button" className="btn-logout" onClick={logout}>Sign out</button>
        </div>
      </form>

      {/* ── Shopping insights ── */}
      <div className="insights-card">
        <h3 className="insights-title">Shopping insights</h3>
        <p className="insights-sub">Learnt from your shopping list activity</p>

        {beh.itemsTracked === 0 ? (
          <p className="insights-empty">No data yet — start adding deals to your shopping list and the AI will learn your preferences automatically.</p>
        ) : (
          <div className="insights-grid">
            <div className="insight-stats-row">
              <div className="insight-stat">
                <span className="insight-stat-value">{beh.itemsTracked}</span>
                <span className="insight-stat-label">Items tracked</span>
              </div>
              <div className="insight-stat">
                <span className="insight-stat-value">{beh.topStores[0] || '—'}</span>
                <span className="insight-stat-label">Favourite store</span>
              </div>
            </div>
            {beh.topCategories.length > 0 && (
              <div className="insight-categories">
                <p className="insight-cat-label">Your top categories</p>
                <div className="insight-cat-chips">
                  {beh.topCategories.map(c => <span key={c} className="insight-cat-chip">{c}</span>)}
                </div>
              </div>
            )}
            {beh.topStores.length > 1 && (
              <div className="insight-categories">
                <p className="insight-cat-label">Where you shop</p>
                <div className="insight-cat-chips">
                  {beh.topStores.map(s => <span key={s} className="insight-cat-chip">{s}</span>)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}
